import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { getSubCategoryIcon, getSubCategoryColor, getTermsIntlNew } from '../../../utils/policy';
import { TERMS_OPTIONS } from '../../../constants/policy';
import { getWebImageUrl } from '../../../utils/image';

const spacer = field => ({
  title: '',
  field,
  formatter: () => '',
  minWidth: 'unset',
  cssClass: 'spacer',
  resizable: false,
  headerSort: false
});

const selections = {
  lifeInsuredSelected: ['N/A', 'Self', 'Not Me', 'No Idea'],
  beneficialSelected: ['N/A', 'Self', 'Not Me', 'No Idea'],
  roomClass: ['Semi Ward', 'Ward', 'Semi Private', 'Private'],
  coverageType: ['Regular', 'Flexi', 'Highend'],
  frequency: ['N/A', 'Per Month', 'Per Quarter', 'Per Half Year', 'Per Year']
};

export const createColumnConfig = ({
  intl,
  currency,
  sorted,
  isCompanyCategory,
  isGeneralCategory,
  selectTwoRows,
  selectBasic,
  selectProtection,
  selectPremium,
  selectSaving,
  selectLifeInsured,
  selectBeneficiary,
  selectRemark,
  selectAccident,
  selectTotalWithdrawals,
  selectLumpSum,
  selectCurrencyPerYear,
  version,
  viewingClientId,
  onEditPolicy,
  getCategory,
  template,
  onAddPolicy
}) => {
  const terms = Object.values(TERMS_OPTIONS).map(term => getTermsIntlNew(intl, term));
  terms.pop();

  const formatters = {
    subCategory: cell => {
      const value = cell.getValue();
      const data = cell.getRow().getData();
      const subCategoryColor = getSubCategoryColor({ _id: data.subCategoryId });
      if (value) {
        return ReactDOMServer.renderToString(
          <div
            style={{
              borderRight: data.subCategoryId === 'total' ? '' : `4px solid ${subCategoryColor}`,
              fontWeight: data.subCategoryId === 'total' ? 600 : 400,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <>
              {(sorted || data.isFirst) &&
                data.subCategoryId !== 'total' &&
                getSubCategoryIcon({ _id: data.subCategoryId }, true)}
              {(sorted || data.isFirst) && (
                <span style={{ marginLeft: '4px', color: subCategoryColor }}>{value}</span>
              )}
            </>
          </div>
        );
      } else {
        return '';
      }
    },
    subCategoryIconOnly: cell => {
      const value = cell.getValue();
      const data = cell.getRow().getData();
      const subCategoryColor = getSubCategoryColor({ _id: data.subCategoryId });
      if (value) {
        return ReactDOMServer.renderToString(
          <div
            style={{
              borderRight: data.subCategoryId === 'total' ? '' : `4px solid ${subCategoryColor}`,
              fontWeight: data.subCategoryId === 'total' ? 600 : 400,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {(sorted || data.isFirst) &&
              data.subCategoryId !== 'total' &&
              getSubCategoryIcon({ _id: data.subCategoryId }, true)}
            {(sorted || data.isFirst) && data.subCategoryId === 'total' && (
              <span style={{ marginLeft: '4px', color: subCategoryColor }}>{value}</span>
            )}
          </div>
        );
      } else {
        return '';
      }
    },
    policyName: cell => {
      const value = cell.getValue();
      return cell.getRow().getData().isRider
        ? `<span style="margin-right: 4px;">${value}</span><img src="${getWebImageUrl(
          'img/rider_small.png'
        )}" alt="rider" width="20" height="20" />`
        : value;
    },
    coverage: cell => {
      const value = cell.getValue();
      const data = cell.getRow().getData();
      if (value === 'lack') {
        return `<img src="${getWebImageUrl(
          'img/policy-review/PolicyReviewLackCover.svg'
        )}" alt="lack-cover" width="20" height="20" />`;
      } else if (value !== '-' && data.isInsuredNotMe) {
        return `<span style="margin-right: 4px;">${value}</span><img src="${getWebImageUrl(
          'img/notme.png'
        )}" alt="not me" width="20" height="20" />`;
      }
      return value;
    },
    taxDeductible: cell => {
      const value = cell.getValue();
      return value === 'notTaxDeductible' ?
        `<img src="${getWebImageUrl('img/cross.svg')}" alt="not-tax-deductible" width="20" height="20" />` :
        value === 'taxDeductible' ?
          `<img src="${getWebImageUrl('img/check.svg')}" alt="tax-deductible" width="20" height="20" />` :
          value;
    },
    premium: cell => {
      const value = cell.getValue();
      const data = cell.getRow().getData();
      if (data.isNotLevelPremium) {
        return data.isPaidUp
          ? `<s style="margin-right: 4px;">${value}</s><img src="${getWebImageUrl(
            'img/not-level.png'
          )}" alt="premium not level paid up" width="20" height="20" />`
          : `<span style="margin-right: 4px;">${value}</span><img src="${getWebImageUrl(
            'img/not-level.png'
          )}" alt="premium not level" width="20" height="20" />`;
      }
      return value;
    },
    lackIcon: cell => {
      const value = cell.getValue();
      return value === 'lack'
        ? `<img src="${getWebImageUrl(
          'img/policy-review/PolicyReviewLackCover.svg'
        )}" alt="lack-cover" width="20" height="20" />`
        : value;
    },
    lackIconMultiple: (cell) => {
      const value = cell.getValue();
      return value === 'lack'
        ? `<img src="${getWebImageUrl('img/policy-review/PolicyReviewLackCover.svg')}" alt="lack-cover" width="20" height="20" />`
        : value ?
          value.replaceAll("lack", ` <img src="${getWebImageUrl('img/policy-review/PolicyReviewLackCover.svg')}" alt="lack-cover" width="20" height="20" style="margin-left: 8px" />`) :
          "";
    },
    lackText: (cell) => {
      const value = cell.getValue();
      return value === 'lack' ? `<span class='lack-text'>${intl.formatMessage({ id: 'pr-lacks' })}</span>` : value;
    },
    premiumPerYear: cell => {
      const value = cell.getValue();
      return cell.getRow().getData().isPaidUp ? `<s>${value}</s>` : value;
    },
    generalCoverage: (cell) => {
      let data = cell.getRow().getData();
      if (data.generalCoverage) {
        let value = data.generalCoverage;
        value = value.replaceAll("lifeTotal", `<span class='total-text'>${intl.formatMessage({ id: "pr-table-life-protection-sum" }) + intl.formatMessage({ id: ":" })}</span>&nbsp;`);
        value = value.replaceAll("ciTotal", `<span class='total-text'>${intl.formatMessage({ id: "pr-table-ci-protection-sum" }) + intl.formatMessage({ id: ":" })}</span>&nbsp;`);
        return value.replaceAll("lack", ` <img src="${getWebImageUrl('img/policy-review/PolicyReviewLackCover.svg')}" alt="lack-cover" width="16" height="16" style="margin-left: 2px" />`);
      } else {
        let notMeIcon = ` <img src="${getWebImageUrl('img/notme.png')}" alt="not me" width="16" height="16" />`;
        let value = "";
        if (data.lifeCoverage && data.lifeCoverage !== "-") {
          value += `<div><span class='category-tag policy-review-tag'>${intl.formatMessage({ id: "Life" })}</span>${data.lifeCoverage !== '-' && data.isInsuredNotMe ? notMeIcon : ''} <span>${data.lifeCoverage}</span></div>`;
        }
        if (data.ciCoverage && data.ciCoverage !== "-") {
          value += `<div style="margin-top: ${value ? 12 : 0}px"><span class='category-tag policy-review-tag'>${intl.formatMessage({ id: "critical-illness-short" })}</span>${data.lifeCoverage !== '-' && data.isInsuredNotMe ? notMeIcon : ''} <span>${data.ciCoverage}</span></div>`;
        }
        if (data.accidentCoverage && data.accidentCoverage !== "-") {
          value += `<div style="margin-top: ${value ? 12 : 0}px"><span class='category-tag policy-review-tag'>${intl.formatMessage({ id: "Accident" })}</span>${data.lifeCoverage !== '-' && data.isInsuredNotMe ? notMeIcon : ''} <span>${data.accidentCoverage}</span></div>`;
        }

        value = value.replaceAll("lack", ` <img src="${getWebImageUrl('img/policy-review/PolicyReviewLackCover.svg')}" alt="lack-cover" width="16" height="16" style="margin-left: 2px" />`);
        value = `<div>${value}</div>`;
        return value;
      }
    },
    notes: (cell) => {
      let data = cell.getRow().getData();
      let value = "";
      if (data.multiClaim === intl.formatMessage({ id: "y" })) {
        value += `<div class='green-tag policy-review-tag'>${intl.formatMessage({ id: "multi-claim" })}</div>`;
      } else if (data.multiClaim === 'lack') {
        value += `<div style="display: flex; align-items: center"><div class='red-tag policy-review-tag'><span>${intl.formatMessage({ id: "lack" })}</span><span style="white-space: nowrap">
        ${intl.formatMessage({ id: "multi-claim" })}<span></div><img src="${getWebImageUrl('img/policy-review/PolicyReviewLackCover.svg')}" alt="lack-cover" width="16" height="16" style="margin-left: 2px" /></div>`;
      }
      if (data.roomClass && data.roomClass !== "lack" && data.roomClass !== "-") {
        value += `<div class='notes-tag policy-review-tag' style="margin-top: ${value ? 8 : 0}px">${data.roomClass}</div>`;
      }
      if (data.coverageType && data.coverageType !== "lack" && data.coverageType !== "-") {
        value += `<div class='notes-tag policy-review-tag' style="margin-top: ${value ? 8 : 0}px">${data.coverageType}</div>`;
      }
      value = `<div>${value}</div>`;
      return value;
    },
    protectionDetails: (cell) => {
      let data = cell.getRow().getData();
      if (data.protectionDetails) {
        return `<span class='total-text'>${intl.formatMessage({ id: "sub-cat-market-value" }) + intl.formatMessage({ id: ":" })}</span>&nbsp;` + data.protectionDetails;
      } else {
        let value = "";
        if (data.compensation) {
          value += `<div>${data.compensation}</div>`;
        }
        if (data.guaranteeValue) {
          value += `<div style="margin-top: ${value ? 8 : 0}px"><span class='category-tag policy-review-tag'>${intl.formatMessage({ id: "ILAS" })}</span> <span>${data.guaranteeValue === intl.formatMessage({ id: 'y' }) ? '' : data.guaranteeValue}</span></div>`;
        }
        if (data.isAnnuity && !data.isTotalRow) {
          value += `<div style="margin-top: ${value ? 8 : 0}px"><span class='category-tag policy-review-tag'>${intl.formatMessage({ id: "Annuity" })}</span> <span>${data.isAnnuity === intl.formatMessage({ id: 'y' }) ? '' : data.isAnnuity}</span></div>`;
        }
        value = value.replaceAll("lack", ` <img src="${getWebImageUrl('img/policy-review/PolicyReviewLackCover.svg')}" alt="lack-cover" width="16" height="16" style="margin-left: 2px" />`);
        value = `<div>${value}</div>`;
        return value;
      }
    },
    terms: (cell) => {
      let data = cell.getRow().getData();
      let value = "";
      if (data.paymentTerms) {
        value += `<div><span class='terms-tag policy-review-tag'>${intl.formatMessage({ id: "payment" })}</span> <span>${data.paymentTerms}</span></div>`;
      }
      if (data.protectionTerms) {
        value += `<div style="margin-top: ${value ? 8 : 0}px"><span class='terms-tag policy-review-tag'>${intl.formatMessage({ id: "protection" })}</span> <span>${data.protectionTerms}</span></div>`;
      }
      value = `<div>${value}</div>`;
      return value;
    },
    frequency: (cell) => {
      let value = cell.getValue();
      let data = cell.getRow().getData();
      if (data.isTotalRow && value) {
        if (version === "compact") {
          return `<span class='total-text '>${intl.formatMessage({ id: "total-premium" }) + intl.formatMessage({ id: ":" })} </span>&nbsp;` + value;
        } else {
          return intl.formatMessage({ id: "total-premium" }) + intl.formatMessage({ id: ":" }) + value;
        }
      } else {
        return value;
      }
    },
    titleWithCurrency: (cell) => {
      let value = cell.getValue();
      return `<span style="font-weight: 500">${value}</span>${intl.locale === "en" ? " " : ""}<span style="color: rgba(0, 0, 0, 0.54)">${intl.formatMessage({ id: "(" })}${intl.formatMessage({ id: currency })}${intl.formatMessage({ id: ")" })}</span>`;
    }
  };

  const customSorter = (a, b, aRow, bRow, column, dir) => {
    const aIsTotal = aRow.getData().isTotalRow;
    const bIsTotal = bRow.getData().isTotalRow;

    if (aIsTotal && !bIsTotal) return dir === 'asc' ? 1 : -1;
    if (!aIsTotal && bIsTotal) return dir === 'asc' ? -1 : 1;

    if (a < b) return -1;
    if (a > b) return 1;

    return 0;
  };

  const createColumn = (titleId, field, options = {}) => ({
    title: titleId ? intl.formatMessage({ id: titleId }, { currency: intl.formatMessage({ id: currency }) }) : '',
    field,
    headerSort: selectTwoRows ? false : true,
    resizable: true,
    headerHozAlign: "center",
    hozAlign: "center",
    vertAlign: "middle",
    titleFormatter: field !== "policyNumber" && field !== "insurer" && field !== "policyName" ?
      (cell => `<span style="color: rgba(0, 0, 0, 0.54)">${cell.getValue()}</span>`) :
      (cell => `<span style="font-weight: 500">${cell.getValue()}</span>`),
    sorter: customSorter,
    editable: cell => !cell.getRow().getData().isTotalRow && cell.getRow().getData().insurer && viewingClientId,
    ...options
  });

  const createGroup = (title, columns, cssClass, options = {}) => ({
    title: title ? intl.formatMessage({ id: title }) : '',
    titleFormatter: cell => `<span style="font-weight: 500">${cell.getValue()}</span>`,
    columns,
    cssClass,
    headerHozAlign: 'center',
    ...options
  });

  const getInfoColumns = () => {
    let cols = [
      createColumn('', 'subCategory', {
        formatter: formatters.subCategory,
        minWidth: intl.locale === "en" ? 150 : 100
      }),
      createColumn('Policy No.', 'policyNumber', {
        minWidth: intl.locale === "en" ? 95 : 80,
        editor: "input",
        cssClass: 'multiline-cell'
      }),
      createColumn('Insurer', 'insurer', {
        minWidth: 80,
        cssClass: 'multiline-cell'
      }),
      createColumn(isCompanyCategory ? 'Workplace Name' : 'pf-plan-name', 'policyName', {
        formatter: formatters.policyName,
        minWidth: intl.locale === "en" ? 135 : 80,
        editor: "input",
        cssClass: 'multiline-cell'
      })
    ];
    return cols.filter(Boolean);
  };

  const getBasicInfoColumns = (version) => {
    const cols = [
      spacer('basic-info-spacer-left'),
      ...(selectSaving ? [createColumn('Saving', 'saving', {
        minWidth: intl.locale === "en" ? 70 : 50
      })] : []),
      createColumn('sub-eff-date-title', 'policyStartDate', {
        minWidth: intl.locale === "en" ? 120 : 100,
        editor: "date",
        editorParams: { max: new Date().toISOString().split("T")[0] }
      }),
      ...(version !== "compact" ? [createColumn('pr-table-cov-terms', 'protectionTerms', {
        minWidth: intl.locale === "en" ? 135 : 80,
        editor: "list",
        editorParams: { values: ["", ...terms] }
      })] : []),
      ...(version !== "compact" ? [createColumn('Payment Terms', 'paymentTerms', {
        minWidth: intl.locale === "en" ? 130 : 80,
        editor: "list",
        editorParams: { values: ["", ...terms] }
      })] : []),
      ...(version === "compact" ? [createColumn('terms', 'terms', {
        formatter: formatters.terms,
        minWidth: intl.locale === "en" ? 200 : 120,
        hozAlign: "left"
      })] : []),
      createColumn('pf-policy-status', 'policyStatus', {
        formatter: formatters.lackText,
        minWidth: intl.locale === "en" ? 65 : 60
      }),
      ...(selectLifeInsured ? [createColumn('Life Insured', 'lifeInsuredSelected', {
        minWidth: intl.locale === "en" ? 100 : 80,
        editor: "list",
        editorParams: { values: selections.lifeInsuredSelected.map(selection => intl.formatMessage({ id: selection })) }
      })] : []),
      ...(selectBeneficiary ? [createColumn('Beneficiary', 'beneficialSelected', {
        minWidth: intl.locale === "en" ? 100 : 80,
        editor: "list",
        editorParams: { values: selections.beneficialSelected.map(selection => intl.formatMessage({ id: selection })) }
      })] : []),
      ...(selectRemark ? [createColumn('Remark', 'remark', {
        minWidth: intl.locale === "en" ? 120 : 120,
        editor: "input",
        cssClass: 'multiline-cell'
      })] : []),
      spacer('basic-info-spacer-right')
    ];
    return cols.filter(Boolean);
  };

  const getProtectionColumns1 = () => {
    const cols = [
      spacer('protection-spacer-left'),
      createGroup('Life', [
        createColumn('coverage-amount', 'lifeCoverage', {
          formatter: formatters.coverage,
          minWidth: intl.locale === "en" ? 105 : 80,
        })
      ], 'life-group', { titleFormatter: cell => `<span style="color: rgba(0, 0, 0, 0.54)">${cell.getValue()}</span>` }),
      createGroup('Critical Illness', [
        createColumn('coverage-amount', 'ciCoverage', {
          formatter: formatters.coverage,
          minWidth: intl.locale === "en" ? 105 : 80,
        }),
        createColumn('multi-claim', 'multiClaim', {
          formatter: formatters.lackIconMultiple,
          minWidth: intl.locale === "en" ? 100 : 65,
        })
      ], '', { titleFormatter: cell => `<span style="color: rgba(0, 0, 0, 0.54)">${cell.getValue()}</span>` }),
      createGroup('Medical', [
        createColumn('Room Class', 'roomClass', {
          formatter: formatters.lackIcon,
          minWidth: intl.locale === "en" ? 105 : 80,
        }),
        createColumn('Type', 'coverageType', {
          formatter: formatters.lackIcon,
          minWidth: intl.locale === "en" ? 60 : 50,
        })
      ], '', { titleFormatter: cell => `<span style="color: rgba(0, 0, 0, 0.54)">${cell.getValue()}</span>` }),
      ...(selectAccident ? [createGroup('Accident', [
        createColumn('coverage-amount', 'accidentCoverage', {
          formatter: formatters.coverage,
          minWidth: intl.locale === "en" ? 105 : 80,
        })
      ], '', { titleFormatter: cell => `<span style="color: rgba(0, 0, 0, 0.54)">${cell.getValue()}</span>` })] : []),
      spacer('protection-spacer-right')
    ];
    return cols.filter(Boolean);
  };

  const getProtectionColumns2 = () => {
    const cols = [
      spacer('protection-spacer-left'),
      createColumn('coverage-amount', 'compensation', {
        formatter: formatters.lackIcon,
        minWidth: intl.locale === "en" ? 105 : 80,
      }),
      createColumn('ILAS', 'guaranteeValue', {
        formatter: formatters.lackIcon,
        minWidth: intl.locale === "en" ? 55 : 65,
      }),
      createColumn('Annuity', 'isAnnuity', {
        formatter: formatters.lackIcon,
        minWidth: intl.locale === "en" ? 80 : 65,
      }),
      createColumn('Market Value', 'marketValue', {
        minWidth: intl.locale === "en" ? 120 : 100,
      }),
      ...(selectTotalWithdrawals ? [createColumn('total-withdrawals', 'totalWithdrawn', {
        minWidth: intl.locale === "en" ? 120 : 100,
      })] : []),
      spacer('protection-spacer-right')
    ];
    return cols.filter(Boolean);
  };

  const getProtectionColumns3 = () => {
    const cols = [
      spacer('protection-spacer-left'),
      createColumn('coverage-amount', 'generalCoverage', {
        formatter: formatters.generalCoverage,
        minWidth: intl.locale === "en" ? 180 : 140,
        hozAlign: "left",
        cssClass: 'multiline-cell'
      }),
      createColumn('notes', 'notes', {
        formatter: formatters.notes,
        minWidth: intl.locale === "en" ? 120 : 100,
        cssClass: 'multiline-cell'
      }),
      spacer('protection-spacer-right')
    ];
    return cols.filter(Boolean);
  };

  const getProtectionColumns4 = () => {
    const cols = [
      spacer('protection-spacer-left'),
      createColumn('coverage-amount', 'protectionDetails', {
        formatter: formatters.protectionDetails,
        minWidth: intl.locale === "en" ? 180 : 140
      }),
      createColumn('Market Value', 'marketValue', {
        minWidth: intl.locale === "en" ? 120 : 100,
      }),
      ...(selectTotalWithdrawals ? [createColumn('total-withdrawals', 'totalWithdrawn', {
        minWidth: intl.locale === "en" ? 120 : 100,
      })] : []),
      spacer('protection-spacer-right')
    ];
    return cols.filter(Boolean);
  };

  const getPremiumColumns = () => {
    const cols = [
      spacer('premium-spacer-left'),
      createColumn('tax-deductible', 'taxDeductible', {
        formatter: formatters.taxDeductible,
        minWidth: intl.locale === "en" ? 125 : 65,
      }),
      createColumn('Currency', 'currencyTranslation', {
        minWidth: intl.locale === "en" ? 85 : 50,
      }),
      createColumn('amount-per-period', 'premium', {
        formatter: formatters.premium,
        minWidth: intl.locale === "en" ? 140 : 80,
        editor: "input"
      }),
      createColumn('Frequency', 'frequencyTranslation', {
        formatter: formatters.frequency,
        minWidth: intl.locale === "en" ? 100 : 80,
        editor: "list",
        editorParams: { values: selections.frequency.map(selection => intl.formatMessage({ id: selection })) }
      }),
      ...(selectCurrencyPerYear ? [createColumn('currency-per-year', 'premiumPerYear', {
        formatter: formatters.premiumPerYear,
        minWidth: intl.locale === "en" ? 100 : 80
      })] : []),
      ...(selectLumpSum ? [createColumn('LumpSum amount', 'lumpSumAmount', {
        minWidth: intl.locale === "en" ? 100 : 80,
        editor: "input"
      })] : []),
      spacer('premium-spacer-right')
    ];
    return cols.filter(Boolean);
  };

  let editColumn = createColumn("", "edit", {
    formatter: function (cell) {
      let rowData = cell.getRow().getData();
      if (rowData.isTotalRow) {
        return '';
      } else if (rowData.isDummy) {
        return "<img alt='add' src='img/add.svg' width='20' />";
      } else if (rowData.insurer) {
        return rowData.isDraft ? "<img alt='edit draft' src='img/draft.png' width='16' />" : "<img alt='edit' src='img/pen.svg' width='14' />";
      }
    },
    cellClick: function (e, cell) {
      let rowData = cell.getRow().getData();
      if (rowData.isTotalRow) {
        return '';
      } else if (rowData.isDummy) {
        onAddPolicy(getCategory(), rowData);
      } else if (rowData.insurer) {
        const policy = (getCategory().subCategories || [])
          .flatMap(sc => sc.policies || [])
          .find(policy => policy._id === rowData._id);
        if (policy) {
          onEditPolicy(policy);
        }
      }
    },
    width: 40,
    editable: false,
    headerSort: false,
    resizable: false
  });

  if (selectTwoRows) {
    var table1Columns = getInfoColumns();
    if (selectBasic) {
      table1Columns.push(createGroup('pr-table-basic-info', getBasicInfoColumns(version), 'basic-info-group'));
    }
    if (!template) {
      table1Columns.push(editColumn);
    }
    table1Columns = table1Columns.filter(Boolean);

    var table2Columns = [
      createColumn('', 'subCategory', { formatter: formatters.subCategoryIconOnly, minWidth: 60 }),
      createColumn('Policy No.', 'policyNumber', { minWidth: intl.locale === "en" ? 95 : 80, cssClass: 'multiline-cell' }),
      createColumn('Insurer', 'insurer', { minWidth: 80, cssClass: 'multiline-cell' }),
    ];
    if (selectProtection) {
      table2Columns.push(createGroup(
        'pr-table-protection',
        isGeneralCategory ?
          version === "compact" ? getProtectionColumns4() : getProtectionColumns2() :
          version === "compact" ? getProtectionColumns3() : getProtectionColumns1(),
        'protection-group',
        { titleFormatter: formatters.titleWithCurrency }
      ));
    }
    if (selectPremium) {
      table2Columns.push(createGroup('pr-table-premium', getPremiumColumns(), 'premium-group'));
    }
    if (!template) {
      table2Columns.push(editColumn);
    }
    table2Columns = table2Columns.filter(Boolean);
  } else {
    var singleTableColumns = getInfoColumns();
    if (selectBasic) {
      singleTableColumns.push(createGroup('pr-table-basic-info', getBasicInfoColumns(version), 'basic-info-group'));
    }
    if (selectProtection) {
      singleTableColumns.push(createGroup(
        'pr-table-protection',
        isGeneralCategory ?
          version === "compact" ? getProtectionColumns4() : getProtectionColumns2() :
          version === "compact" ? getProtectionColumns3() : getProtectionColumns1(),
        'protection-group',
        { titleFormatter: formatters.titleWithCurrency }
      ));
    }
    if (selectPremium) {
      singleTableColumns.push(createGroup('pr-table-premium', getPremiumColumns(), 'premium-group'));
    }
    if (!template) {
      singleTableColumns.push(editColumn);
    }
    singleTableColumns = singleTableColumns.filter(Boolean);
  }

  return {
    table1: table1Columns,
    table2: table2Columns,
    singleTable: singleTableColumns
  };
};